import React, { useState } from 'react';
import { Card } from '../Shared/Card';
import { Loan, LoanProgram } from '../../types/LoanTypes';
import { FileTextIcon, CheckIcon, AlertTriangleIcon, DownloadIcon, FolderIcon, ArrowRightIcon, AlertCircleIcon, InfoIcon } from 'lucide-react';
import { DBRSExportModal } from './DBRSExportModal';
export function DBRSReporting({
  setActiveTab
}: {
  setActiveTab: (tab: string) => void;
}) {
  const [activeTemplate, setActiveTemplate] = useState('surveillance');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedLoanProgram, setSelectedLoanProgram] = useState<LoanProgram>('RTL');
  // Only RTL loans should be shown in DBRS reporting
  const rtlLoans: Loan[] = [{
    id: '#005312',
    borrower: 'Alex Young',
    type: 'Fix-and-flip',
    loanProgram: 'RTL',
    amount: '$500,000',
    fico: 720,
    ltv: '67%',
    dscr: '1.25',
    status: 'Performing',
    pool: 'Q3 RTL Pool',
    eligibleForDBRS: true
  }, {
    id: '#005331',
    borrower: 'James Wilson',
    type: 'Bridge',
    loanProgram: 'RTL',
    amount: '$1,200,000',
    fico: 680,
    ltv: '65%',
    dscr: '1.30',
    status: 'Late Payment',
    pool: 'Bridge Loan Package',
    eligibleForDBRS: true
  }, {
    id: '#005342',
    borrower: 'Sarah Miller',
    type: 'Fix-and-flip',
    loanProgram: 'RTL',
    amount: '$425,000',
    fico: 710,
    ltv: '70%',
    dscr: '1.20',
    status: 'Performing',
    pool: 'Unassigned',
    eligibleForDBRS: true
  }];
  const templates = [{
    id: 'surveillance',
    name: 'Surveillance Report',
    progress: 92,
    dueDate: 'May 15, 2023'
  }, {
    id: 'revolver',
    name: 'Revolver Performance',
    progress: 78,
    dueDate: 'May 30, 2023'
  }, {
    id: 'performance',
    name: 'Monthly Performance',
    progress: 85,
    dueDate: 'May 10, 2023'
  }];
  const fieldGroups = [{
    name: 'Loan Information',
    fields: [{
      name: 'Loan ID',
      status: 'complete'
    }, {
      name: 'Original Balance',
      status: 'complete'
    }, {
      name: 'Current Balance',
      status: 'complete'
    }, {
      name: 'Origination Date',
      status: 'complete'
    }, {
      name: 'Maturity Date',
      status: 'complete'
    }],
    completion: 100
  }, {
    name: 'Borrower Information',
    fields: [{
      name: 'Borrower Name',
      status: 'complete'
    }, {
      name: 'Borrower Entity Type',
      status: 'complete'
    }, {
      name: 'Guarantor FICO',
      status: 'complete'
    }, {
      name: 'Liquidity Verification',
      status: 'complete'
    }, {
      name: 'Experience Documentation',
      status: 'exception',
      note: 'Missing 2 properties from experience history'
    }],
    completion: 80
  }, {
    name: 'Property Information',
    fields: [{
      name: 'Property Address',
      status: 'complete'
    }, {
      name: 'Property Type',
      status: 'complete'
    }, {
      name: 'Number of Units',
      status: 'complete'
    }, {
      name: 'Square Footage',
      status: 'complete'
    }, {
      name: 'Year Built',
      status: 'complete'
    }],
    completion: 100
  }, {
    name: 'Valuation & Underwriting',
    fields: [{
      name: 'Appraised Value',
      status: 'complete'
    }, {
      name: 'Purchase Price',
      status: 'complete'
    }, {
      name: 'LTV',
      status: 'exception',
      note: 'LTV exceeds 70% guideline (73%)'
    }, {
      name: 'DSCR',
      status: 'complete'
    }, {
      name: 'Debt Yield',
      status: 'missing'
    }],
    completion: 80
  }];
  // RTL-specific DBRS eligibility criteria
  const dbrsEligibilityCriteria = [{
    name: 'FICO Score',
    requirement: 'Minimum 660',
    icon: <CheckIcon className="w-4 h-4 text-green-500" data-id="element-372" />
  }, {
    name: 'Property Type',
    requirement: 'SFR, 2-4 Unit, Condo, Townhome',
    icon: <CheckIcon className="w-4 h-4 text-green-500" data-id="element-373" />
  }, {
    name: 'Max LTV',
    requirement: '80% for SFR, 75% for others',
    icon: <CheckIcon className="w-4 h-4 text-green-500" data-id="element-374" />
  }, {
    name: 'Experience',
    requirement: 'At least 1 completed project',
    icon: <AlertTriangleIcon className="w-4 h-4 text-amber-500" data-id="element-375" />
  }, {
    name: 'Loan Term',
    requirement: '6-24 months',
    icon: <CheckIcon className="w-4 h-4 text-green-500" data-id="element-376" />
  }, {
    name: 'Loan Purpose',
    requirement: 'Renovation, Rehab, or Bridge',
    icon: <CheckIcon className="w-4 h-4 text-green-500" data-id="element-377" />
  }];
  const openExportModal = () => {
    setIsExportModalOpen(true);
  };
  const closeExportModal = () => {
    setIsExportModalOpen(false);
  };
  const handleAttachToDataRoom = () => {
    alert('Attaching to data room...');
  };
  const handleViewException = () => {
    setActiveTab('exceptions');
  };
  const handleResolveField = () => {
    alert('Opening field resolution form...');
  };
  return <div className="space-y-6" data-id="element-378">
      <div className="flex justify-between items-center" data-id="element-379">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-380">
          DBRS Reporting Readiness
        </h2>
        <div className="flex space-x-3" data-id="element-381">
          <button className="px-4 py-2 bg-background border border-border rounded-full text-foreground/70 hover:bg-muted flex items-center text-sm transition-colors" onClick={handleAttachToDataRoom} data-id="element-382">
            <FolderIcon className="w-4 h-4 mr-2" data-id="element-383" />
            Attach to Data Room
          </button>
          <button className="px-4 py-2 bg-success rounded-full text-foreground/70 hover:bg-success/80 flex items-center text-sm transition-colors" onClick={() => setSelectedLoanProgram('RTL')} data-id="element-384">
            Switch to RTL Loans
          </button>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-md flex items-start" data-id="element-385">
        <InfoIcon className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-386" />
        <div data-id="element-387">
          <h3 className="font-medium text-blue-800" data-id="element-388">DBRS Eligibility Notice</h3>
          <p className="text-sm text-blue-700 mt-1" data-id="element-389">
            Only RTL (Residential Transition Loans) are eligible for DBRS-rated
            securitizations.
          </p>
        </div>
      </div>
      {selectedLoanProgram === 'DSCR' && <div className="bg-red-50 border border-red-200 p-4 rounded-md flex items-start" data-id="element-390">
          <AlertCircleIcon className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-391" />
          <div data-id="element-392">
            <h3 className="font-medium text-red-800" data-id="element-393">
              Not Available for DSCR Loans
            </h3>
            <p className="text-sm text-red-700 mt-1" data-id="element-394">
              Please switch to RTL loans to continue with DBRS reporting.
            </p>
            <button className="mt-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700" onClick={() => setSelectedLoanProgram('RTL')} data-id="element-395">
              Switch to RTL Loans
            </button>
          </div>
        </div>}
      <div className="flex items-center mb-4" data-id="element-396">
        <span className="text-sm font-medium text-gray-700 mr-2" data-id="element-397">
          Loan Program:
        </span>
        <div className="flex rounded-md overflow-hidden border border-gray-300" data-id="element-398">
          <button className={`px-3 py-1 text-sm ${selectedLoanProgram === 'RTL' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setSelectedLoanProgram('RTL')} data-id="element-399">
            RTL Loans
          </button>
          <button className={`px-3 py-1 text-sm ${selectedLoanProgram === 'DSCR' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={() => setSelectedLoanProgram('DSCR')} data-id="element-400">
            DSCR Loans
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-6" data-id="element-401">
        <div className="col-span-1 space-y-6" data-id="element-402">
          <Card data-id="element-403">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-404">
              DBRS Templates
            </h3>
            {selectedLoanProgram === 'RTL' ? <div className="space-y-4" data-id="element-405">
                {templates.map(template => <div key={template.id} className={`p-4 rounded-lg border ${activeTemplate === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'} cursor-pointer`} onClick={() => setActiveTemplate(template.id)} data-id="element-406">
                    <div className="flex justify-between items-start" data-id="element-407">
                      <div data-id="element-408">
                        <h4 className="font-medium text-gray-900" data-id="element-409">
                          {template.name}
                        </h4>
                        <p className="text-sm text-gray-500" data-id="element-410">
                          Due: {template.dueDate}
                        </p>
                      </div>
                      <div className="flex items-center" data-id="element-411">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2" data-id="element-412">
                          <div className={`h-2 rounded-full ${template.progress >= 90 ? 'bg-green-500' : template.progress >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                      width: `${template.progress}%`
                    }} data-id="element-413"></div>
                        </div>
                        <span className="text-xs font-medium text-gray-900" data-id="element-414">
                          {template.progress}%
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="p-4 bg-gray-50 border border-gray-200 rounded-md" data-id="element-415">
                <p className="text-sm text-gray-500" data-id="element-416">
                  DBRS templates are not applicable for DSCR loans.
                </p>
              </div>}
          </Card>
          {selectedLoanProgram === 'RTL' && <Card data-id="element-417">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-418">
                Template Summary
              </h3>
              <div className="space-y-4" data-id="element-419">
                <div data-id="element-420">
                  <div className="flex justify-between mb-1" data-id="element-421">
                    <span className="text-sm font-medium text-gray-700" data-id="element-422">
                      Overall Completion
                    </span>
                    <span className="text-sm font-medium text-gray-700" data-id="element-423">
                      92%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-424">
                    <div className="bg-green-500 h-2.5 rounded-full" style={{
                  width: '92%'
                }} data-id="element-425"></div>
                  </div>
                </div>
                <div className="pt-2" data-id="element-426">
                  <div className="flex justify-between items-center text-sm text-gray-600 mb-2" data-id="element-427">
                    <span data-id="element-428">Fields Status</span>
                    <span data-id="element-429">Count</span>
                  </div>
                  <div className="space-y-2" data-id="element-430">
                    <div className="flex justify-between items-center" data-id="element-431">
                      <div className="flex items-center" data-id="element-432">
                        <CheckIcon className="w-4 h-4 text-green-500 mr-2" data-id="element-433" />
                        <span className="text-sm text-gray-700" data-id="element-434">Complete</span>
                      </div>
                      <span className="text-sm font-medium" data-id="element-435">83</span>
                    </div>
                    <div className="flex justify-between items-center cursor-pointer" onClick={handleViewException} data-id="element-436">
                      <div className="flex items-center" data-id="element-437">
                        <AlertTriangleIcon className="w-4 h-4 text-amber-500 mr-2" data-id="element-438" />
                        <span className="text-sm text-gray-700" data-id="element-439">
                          Exceptions
                        </span>
                      </div>
                      <span className="text-sm font-medium" data-id="element-440">5</span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-441">
                      <div className="flex items-center" data-id="element-442">
                        <FileTextIcon className="w-4 h-4 text-red-500 mr-2" data-id="element-443" />
                        <span className="text-sm text-gray-700" data-id="element-444">Missing</span>
                      </div>
                      <span className="text-sm font-medium" data-id="element-445">2</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>}
          {selectedLoanProgram === 'RTL' && <Card data-id="element-446">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-447">
                DBRS Eligibility Criteria
              </h3>
              <div className="space-y-3" data-id="element-448">
                {dbrsEligibilityCriteria.map((criteria, index) => <div key={index} className="flex justify-between items-center" data-id="element-449">
                    <div className="flex items-center" data-id="element-450">
                      {criteria.icon}
                      <span className="text-sm text-gray-700 ml-2" data-id="element-451">
                        {criteria.name}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-452">
                      {criteria.requirement}
                    </span>
                  </div>)}
              </div>
            </Card>}
        </div>
        {selectedLoanProgram === 'RTL' ? <Card className="col-span-2" data-id="element-453">
            <div className="flex justify-between items-center mb-6" data-id="element-454">
              <h3 className="text-lg font-medium text-gray-800" data-id="element-455">
                Field Validation
              </h3>
              <div className="flex items-center" data-id="element-456">
                <input type="text" placeholder="Search fields..." className="border border-gray-300 rounded-md px-3 py-1.5 mr-2" data-id="element-457" />
                <select className="border border-gray-300 rounded-md px-3 py-1.5" data-id="element-458">
                  <option data-id="element-459">All Statuses</option>
                  <option data-id="element-460">Complete</option>
                  <option data-id="element-461">Exceptions</option>
                  <option data-id="element-462">Missing</option>
                </select>
              </div>
            </div>
            <div className="space-y-6" data-id="element-463">
              {fieldGroups.map(group => <div key={group.name} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-464">
                  <div className="flex justify-between items-center bg-gray-50 px-6 py-3 border-b border-gray-200" data-id="element-465">
                    <h4 className="font-medium text-gray-800" data-id="element-466">{group.name}</h4>
                    <div className="flex items-center" data-id="element-467">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-2" data-id="element-468">
                        <div className={`h-2 rounded-full ${group.completion === 100 ? 'bg-green-500' : group.completion >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                    width: `${group.completion}%`
                  }} data-id="element-469"></div>
                      </div>
                      <span className="text-xs font-medium text-gray-900" data-id="element-470">
                        {group.completion}%
                      </span>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-200" data-id="element-471">
                    {group.fields.map(field => <div key={field.name} className="px-6 py-4 flex justify-between items-center" data-id="element-472">
                        <div className="flex items-center" data-id="element-473">
                          {field.status === 'complete' && <CheckIcon className="w-5 h-5 text-green-500 mr-3" data-id="element-474" />}
                          {field.status === 'exception' && <AlertTriangleIcon className="w-5 h-5 text-amber-500 mr-3" data-id="element-475" />}
                          {field.status === 'missing' && <FileTextIcon className="w-5 h-5 text-red-500 mr-3" data-id="element-476" />}
                          <span className="text-sm text-gray-800" data-id="element-477">
                            {field.name}
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-478">
                          {field.status === 'exception' && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mr-3" data-id="element-479">
                              {field.note}
                            </span>}
                          {field.status === 'missing' && <button className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded flex items-center" onClick={handleResolveField} data-id="element-480">
                              Resolve{' '}
                              <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-481" />
                            </button>}
                          {field.status === 'exception' && <button className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded flex items-center" onClick={handleViewException} data-id="element-482">
                              View Exception{' '}
                              <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-483" />
                            </button>}
                        </div>
                      </div>)}
                  </div>
                </div>)}
            </div>
          </Card> : <Card className="col-span-2" data-id="element-484">
            <div className="flex justify-center items-center h-64 flex-col" data-id="element-485">
              <AlertCircleIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-486" />
              <h3 className="text-xl font-medium text-gray-500 mb-2" data-id="element-487">
                DBRS Not Available for DSCR Loans
              </h3>
              <p className="text-gray-500 text-center max-w-md" data-id="element-488">
                DBRS ratings are only available for RTL (short-term) loans.
              </p>
              <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => setSelectedLoanProgram('RTL')} data-id="element-489">
                Switch to RTL Loans
              </button>
            </div>
          </Card>}
      </div>
      <DBRSExportModal isOpen={isExportModalOpen} onClose={closeExportModal} templateType="DBRS Surveillance" reportingPeriod="Q3 2025" data-id="element-490" />
    </div>;
}